/**
 * URL slug derivation for adhigarams.
 *
 * Slug rule:
 *   1. Lowercase ``title_en``, replace runs of non-alphanumerics with a
 *      single hyphen, trim leading/trailing hyphens.
 *   2. If two or more adhigarams collapse to the same base slug (only
 *      "Ignorance" — n=41 and n=85 — as of writing), append ``-<n>`` to
 *      each colliding entry. Non-colliding slugs stay clean.
 *
 * The same rule is duplicated in ``ui/scripts/generate-sitemap.mjs`` so
 * the sitemap and the client agree on every URL. Any change here must
 * be paired with the same change there.
 */

import type { AdhigaramEntry } from './types';

export function baseSlug(titleEn: string): string {
  return titleEn
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface AdhigaramSlugEntry {
  n: number;
  slug: string;
}

/** Return one slug per adhigaram, in the same order as the input. */
export function buildAdhigaramSlugs(
  adhigarams: readonly Pick<AdhigaramEntry, 'n' | 'title_en'>[]
): AdhigaramSlugEntry[] {
  const counts = new Map<string, number>();
  for (const a of adhigarams) {
    const s = baseSlug(a.title_en);
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  return adhigarams.map((a) => {
    const s = baseSlug(a.title_en);
    const slug = (counts.get(s) ?? 0) > 1 ? `${s}-${a.n}` : s;
    return { n: a.n, slug };
  });
}

/** Map from slug → adhigaram_n, for O(1) route param lookup. */
export function adhigaramSlugToN(
  adhigarams: readonly Pick<AdhigaramEntry, 'n' | 'title_en'>[]
): Map<string, number> {
  const out = new Map<string, number>();
  for (const { n, slug } of buildAdhigaramSlugs(adhigarams)) {
    out.set(slug, n);
  }
  return out;
}

/** Map from adhigaram_n → slug, for building links from a known n. */
export function adhigaramNToSlug(
  adhigarams: readonly Pick<AdhigaramEntry, 'n' | 'title_en'>[]
): Map<number, string> {
  const out = new Map<number, string>();
  for (const { n, slug } of buildAdhigaramSlugs(adhigarams)) {
    out.set(n, slug);
  }
  return out;
}
